import type { ITableColumnInfo } from '@/pages/datalab/components/CustomComponents/AliTableAnalytics/types';

const MAX_WIDTH_SAMPLE_SIZE = 200;
const WIDTH_CHAR_PIXEL = 9;
// 基础内边距 + 预留排序/拖拽等交互空间，避免估算宽度过紧。
const WIDTH_BASE_PADDING = 32 + 10;
const MIN_NORMAL_COLUMN_WIDTH = 120;
const MAX_NORMAL_COLUMN_WIDTH = 280;

type TColumnWidthRange = {
  min: number;
  max: number;
};

type TLocaleColumnWidthRange = {
  zh: TColumnWidthRange;
  en: TColumnWidthRange;
};

const DEFAULT_COLUMN_WIDTH_RANGE: TLocaleColumnWidthRange = {
  zh: {
    min: MIN_NORMAL_COLUMN_WIDTH,
    max: MAX_NORMAL_COLUMN_WIDTH,
  },
  en: {
    min: 108,
    max: 260,
  },
};

// 业务字段的宽度策略：按关键字命中，且中英文分别配置范围。
const COLUMN_WIDTH_RANGE_MAP: Record<string, TLocaleColumnWidthRange> = {
  date: {
    zh: { min: 96, max: 140 },
    en: { min: 100, max: 150 },
  },
  '日期': {
    zh: { min: 96, max: 140 },
    en: { min: 100, max: 150 },
  },
  platform: {
    zh: { min: 80, max: 132 },
    en: { min: 92, max: 150 },
  },
  '平台': {
    zh: { min: 80, max: 132 },
    en: { min: 92, max: 150 },
  },
  country: {
    zh: { min: 80, max: 132 },
    en: { min: 96, max: 160 },
  },
  '国家': {
    zh: { min: 80, max: 132 },
    en: { min: 96, max: 160 },
  },
  version: {
    zh: { min: 80, max: 132 },
    en: { min: 92, max: 140 },
  },
  '版本': {
    zh: { min: 80, max: 132 },
    en: { min: 92, max: 140 },
  },
  language: {
    zh: { min: 80, max: 132 },
    en: { min: 96, max: 150 },
  },
  lang: {
    zh: { min: 80, max: 132 },
    en: { min: 96, max: 150 },
  },
  '语言': {
    zh: { min: 80, max: 132 },
    en: { min: 96, max: 150 },
  },
  channel: {
    zh: { min: 92, max: 140 },
    en: { min: 104, max: 170 },
  },
  network: {
    zh: { min: 92, max: 140 },
    en: { min: 104, max: 170 },
  },
  '渠道': {
    zh: { min: 92, max: 140 },
    en: { min: 104, max: 170 },
  },
  observation_days: {
    zh: { min: 80, max: 120 },
    en: { min: 92, max: 136 },
  },
  '观测天数': {
    zh: { min: 80, max: 120 },
    en: { min: 92, max: 136 },
  },
  day: {
    zh: { min: 80, max: 120 },
    en: { min: 92, max: 130 },
  },
};

const getVisualTextLength = (text: string): number => {
  // 中文按 2 个“可视字符”计，英文/数字按 1 个计，估算更贴近真实占宽。
  return Array.from(text).reduce((acc, char) => {
    return acc + (/[^\x00-\xff]/.test(char) ? 2 : 1);
  }, 0);
};

const normalizeColumnKey = (...inputs: Array<string | undefined>): string => {
  return inputs
    .filter(Boolean)
    .join('|')
    .toLowerCase();
};

const getColumnWidthRange = (
  key: string,
  isEn: boolean,
): TColumnWidthRange => {
  const localeKey = isEn ? 'en' : 'zh';
  // day1/day2/... 统一归入 day 范围，避免为每个 dayN 单独配置。
  const exactDayPattern = /\bday\d+\b/i;
  if (exactDayPattern.test(key)) {
    return COLUMN_WIDTH_RANGE_MAP.day[localeKey];
  }
  const matched = Object.entries(COLUMN_WIDTH_RANGE_MAP).find(([mapKey]) => {
    if (mapKey === 'day') {
      return false;
    }
    return key.includes(mapKey);
  });
  return matched?.[1][localeKey] ?? DEFAULT_COLUMN_WIDTH_RANGE[localeKey];
};

const clampWidth = (width: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, width));
};

export const buildAdaptiveColumnWidths = (
  columns: ITableColumnInfo[],
  rows: Array<Record<string, any>>,
  isEn: boolean,
): number[] => {
  // 只采样前 N 行，平衡“宽度准确性”与“渲染性能”。
  const sampleRows = (rows || []).slice(0, MAX_WIDTH_SAMPLE_SIZE);
  return columns.map((column) => {
    const columnTitle = String(
      isEn
        ? column?.name_en || column?.name_cn || column?.name
        : column?.name_cn || column?.name_en || column?.name,
    );
    const columnKey = normalizeColumnKey(
      column?.origin_name,
      column?.name,
      column?.name_cn,
      column?.name_en,
    );
    const { min: minWidth, max: maxWidth } = getColumnWidthRange(
      columnKey,
      isEn,
    );

    let maxVisualLength = getVisualTextLength(columnTitle);
    sampleRows.forEach((row) => {
      const valueCell = row?.[column?.name || ''];
      if (!valueCell || typeof valueCell !== 'object') {
        // 兜底：兼容非标准结构（值不是 { value, value_en, value_cn } 对象）。
        const fallbackText = String(valueCell ?? '');
        maxVisualLength = Math.max(
          maxVisualLength,
          getVisualTextLength(fallbackText),
        );
        return;
      }
      // 单元格宽度估算也按当前语言优先读取，标题和内容的语言口径保持一致。
      const rawValue =
        (isEn ? valueCell?.value_en : valueCell?.value_cn) ||
        valueCell?.value ||
        (isEn ? valueCell?.value_cn : valueCell?.value_en) ||
        '';
      const text = String(rawValue ?? '');
      maxVisualLength = Math.max(maxVisualLength, getVisualTextLength(text));
    });

    const estimatedWidth = maxVisualLength * WIDTH_CHAR_PIXEL + WIDTH_BASE_PADDING;
    return clampWidth(estimatedWidth, minWidth, maxWidth);
  });
};

